/**
 * Section-lockdown logic for the Patient Edit page.
 *
 * A2 semantics: while ONE section is a new-history draft (and we are not in the
 * "まとめて履歴作成" bulk flow), the other sections are disabled so the user can
 * only edit the section they are creating a history for.
 *
 * The "is this section a draft?" signal comes from the URL: an empty
 * `patient_information_id` / `health_insurance_card_id` / `medical_aid_id` query
 * value means that section is in new-draft mode.
 *
 * IMPORTANT (HIT-4723): an empty `medical_aid_id` is ambiguous — it can mean
 * "the user is drafting a new 医療助成 history" OR simply "the patient has no
 * 医療助成 at all". The 医療助成 history menu (and therefore the new-draft action)
 * only renders when the patient has at least one 医療助成 record, so for a patient
 * with zero 医療助成 an empty `medical_aid_id` can never be a real draft. Treating
 * it as one wrongly flagged the page as "a section is being drafted" and locked
 * the 住所/保険証 sections whenever such a patient switched/opened a history.
 */
export interface PatientEditSectionLockdownInput {
  /** URL `patient_information_id` is present & empty (new 受診者情報 draft). */
  patientInformationHistoryDraftByUrl: boolean;
  /** URL `health_insurance_card_id` is present & empty (new 保険証 draft). */
  healthInsuranceHistoryDraftByUrl: boolean;
  /** URL `medical_aid_id` is present & empty. */
  medicalAidHistoryDraftByUrl: boolean;
  /** Currently selected 医療助成 種別 in the form (なし === 1). */
  medicalAidInsurerTypeId: number | null | undefined;
  /** Number of persisted 医療助成 records for this patient. */
  medicalAidRecordCount: number;
}

export interface PatientEditSectionLockdown {
  disablePatientInfo: boolean;
  disableInsurance: boolean;
  disableMedicalAid: boolean;
  disableOther: boolean;
}

export function computePatientEditSectionLockdown(
  input: PatientEditSectionLockdownInput
): PatientEditSectionLockdown {
  const {
    patientInformationHistoryDraftByUrl,
    healthInsuranceHistoryDraftByUrl,
    medicalAidHistoryDraftByUrl,
    medicalAidInsurerTypeId,
    medicalAidRecordCount,
  } = input;

  const hasNoMedicalAid = medicalAidRecordCount <= 0;

  // An empty `medical_aid_id` only represents an in-progress 医療助成 history draft
  // when the patient actually has 医療助成 records (only then can the history menu
  // offer the "新しい情報を入力する" draft action). For patients with no 医療助成 it
  // is just "no medical aid" and must NOT lock the other sections. (HIT-4723)
  const medicalAidSectionInDraft = medicalAidHistoryDraftByUrl && !hasNoMedicalAid;

  const isAnySectionDraft =
    patientInformationHistoryDraftByUrl ||
    healthInsuranceHistoryDraftByUrl ||
    medicalAidSectionInDraft;

  // Bulk ("まとめて") draft = PI + 保険証 are both drafts, and 医療助成 is either also
  // a draft, is 種別 なし (insurer_type 1 — no MA history created), or the patient has
  // no 医療助成 at all (nothing to draft, but still a valid bulk run).
  const isBulkMode =
    patientInformationHistoryDraftByUrl &&
    healthInsuranceHistoryDraftByUrl &&
    (medicalAidSectionInDraft || medicalAidInsurerTypeId === 1 || hasNoMedicalAid);

  const disablePatientInfo =
    isAnySectionDraft && !isBulkMode && !patientInformationHistoryDraftByUrl;
  const disableInsurance =
    isAnySectionDraft && !isBulkMode && !healthInsuranceHistoryDraftByUrl;
  const disableMedicalAid = isAnySectionDraft && !isBulkMode && !medicalAidSectionInDraft;
  const disableOther = disablePatientInfo; // Part of patient information

  return { disablePatientInfo, disableInsurance, disableMedicalAid, disableOther };
}
